import React, { useState } from "react";
import { Plus, RotateCw } from "lucide-react";
import { finApi, finPref, money } from "@/lib/financeApi";
import { btnGold, Chip, Empty, ErrorBox, Loading, PAYOUT_CHIP, StatusFilter, SummaryChip, useLoad } from "@/components/nds/windows/finance/parts";
import PayoutActions from "@/components/nds/windows/finance/PayoutActions";
import PayoutForm from "@/components/nds/windows/finance/PayoutForm";
import FinPayoutDetail from "@/components/nds/windows/finance/FinPayoutDetail";

const label = (s) => s.replace(/_/g, " ");

export default function FinSessionPayoutsContent() {
  const [status, setStatusState] = useState(() => finPref.get("payouts_status", "all"));
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState(null);
  const setStatus = (v) => { finPref.set("payouts_status", v); setStatusState(v); };
  const [state, load] = useLoad(() => Promise.all([finApi.get("/session-payouts"), finApi.get("/session-payouts/summary")])
    .then(([l, s]) => ({ payouts: l.payouts, summary: s })), []);

  if (selected) return <FinPayoutDetail payoutId={selected} onBack={() => { setSelected(null); load(); }} />;
  if (state.loading && !state.data) return <Loading testid="fin-payouts-loading" label="Loading payouts…" />;
  if (state.error) return <ErrorBox testid="fin-payouts-error" error={state.error} onRetry={load} />;
  const { payouts, summary } = state.data;
  const counts = { all: payouts.length };
  payouts.forEach((p) => { counts[p.status] = (counts[p.status] || 0) + 1; });
  const list = status === "all" ? payouts : payouts.filter((p) => p.status === status);

  return (
    <div data-testid="fin-payouts-content" className="flex flex-col h-full">
      <div className="px-5 py-3 border-b border-white/10 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <SummaryChip tone="blue" testid="fin-payouts-summary">{summary.approved_count} approved · {money(summary.approved_total)} to pay</SummaryChip>
          {!adding && <button type="button" onClick={() => setAdding(true)} data-testid="fin-payout-new" className={`${btnGold} inline-flex items-center gap-1.5 ml-auto`}><Plus className="h-3 w-3" /> New payout</button>}
          <button type="button" onClick={load} data-testid="fin-payouts-refresh" aria-label="Refresh" className={`p-1.5 text-nex-muted hover:text-nex-gold ${adding ? "ml-auto" : ""}`}><RotateCw className="h-3.5 w-3.5" /></button>
        </div>
        <StatusFilter prefix="fin-payouts" chips={PAYOUT_CHIP} value={status} onChange={setStatus} counts={counts} />
      </div>
      {adding && <div className="px-5 pt-3"><PayoutForm onCancel={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} /></div>}
      {list.length ? (
        <ul data-testid="fin-payouts-list" className="flex-1 overflow-y-auto divide-y divide-white/5">
          {list.map((p) => {
            const [tone, l] = PAYOUT_CHIP[p.status];
            return (
              <li key={p.id} data-testid={`fin-payout-row-${p.id}`} onClick={() => setSelected(p.id)} className="px-5 py-3 space-y-2 cursor-pointer hover:bg-nex-gold/5 transition-colors">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-nex-silver font-medium">{p.staff_name}</span>
                  <span className="font-mono text-[10px] text-nex-muted">{p.session_date}</span>
                  <span className="text-xs text-nex-muted capitalize">{label(p.session_type)}</span>
                  <span className="font-mono text-[10px] text-nex-muted">{p.hours}h × {money(p.rate_per_hour)} · {p.resident_ids.length} resident(s)</span>
                  <Chip tone={tone} data-testid={`fin-payout-status-${p.id}`}>{l}</Chip>
                  <span data-testid={`fin-payout-gross-${p.id}`} className="ml-auto font-mono text-sm text-nex-gold">{money(p.gross_amount)}</span>
                </div>
                <PayoutActions p={p} onChanged={load} />
              </li>
            );
          })}
        </ul>
      ) : <Empty testid="fin-payouts-empty">No payouts{status !== "all" ? ` with status ${status}` : ""} yet.</Empty>}
    </div>
  );
}
